import { useEffect, useState } from 'react';
import { getConsent, saveConsent, acceptAll, rejectAll, type ConsentState } from '../lib/consent';
import { useStore } from '../store';
import { SM } from '../lib/theme';

const T = {
  en: {
    bannerText:
      'We use cookies to improve your experience. Analytics cookies help us understand how TraderYo is used.',
    privacyLink: 'Privacy Policy',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    manage: 'Manage Preferences',
    modalTitle: 'Cookie Preferences',
    modalSub: 'Choose which cookies you allow. You can change this anytime in Settings.',
    essential: 'Essential Cookies',
    essentialDesc: 'Required for login, session, and core app functionality. Cannot be disabled.',
    analytics: 'Analytics Cookies',
    analyticsDesc:
      'Google Analytics (GA4) — helps us understand usage and improve the app. No personal data sold.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'Future: Facebook Pixel and similar advertising trackers. Currently not active.',
    alwaysOn: 'Always on',
    save: 'Save Preferences',
    cookieSettings: 'Cookie Settings',
  },
  he: {
    bannerText:
      'אנו משתמשים בעוגיות לשיפור החוויה שלך. עוגיות ניתוח עוזרות לנו להבין כיצד TraderYo משמש.',
    privacyLink: 'מדיניות פרטיות',
    acceptAll: 'קבל הכל',
    rejectAll: 'דחה הכל',
    manage: 'נהל העדפות',
    modalTitle: 'העדפות עוגיות',
    modalSub: 'בחר אילו עוגיות אתה מאפשר. ניתן לשנות זאת בכל עת בהגדרות.',
    essential: 'עוגיות חיוניות',
    essentialDesc: 'נדרשות לכניסה, סשן ופונקציונליות ליבה של האפליקציה. לא ניתן להשבית.',
    analytics: 'עוגיות ניתוח',
    analyticsDesc:
      'Google Analytics (GA4) — עוזר לנו להבין שימוש ולשפר את האפליקציה. לא נמכרים נתונים אישיים.',
    marketing: 'עוגיות שיווק',
    marketingDesc: 'עתידי: Facebook Pixel ומעקבי פרסום דומים. כרגע לא פעיל.',
    alwaysOn: 'תמיד פעיל',
    save: 'שמור העדפות',
    cookieSettings: 'הגדרות עוגיות',
  },
};

type Lang = 'en' | 'he';

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 999,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        background: checked ? SM.primary : '#d1d5db',
        position: 'relative',
        flexShrink: 0,
        transition: 'background .15s',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 23 : 3,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .15s',
          boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        }}
      />
    </button>
  );
}

function CategoryRow({
  title,
  desc,
  checked,
  onChange,
  disabled,
  alwaysOnLabel,
}: {
  title: string;
  desc: string;
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  alwaysOnLabel?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
        padding: '14px 0',
        borderBottom: `1px solid ${SM.border}`,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: SM.text, marginBottom: 4 }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: SM.textDim, lineHeight: 1.55 }}>{desc}</div>
      </div>
      {disabled ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: SM.primary,
            background: SM.primarySoft,
            borderRadius: 999,
            padding: '3px 10px',
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
        >
          {alwaysOnLabel}
        </span>
      ) : (
        <Toggle checked={checked} onChange={onChange} />
      )}
    </div>
  );
}

export default function CookieConsent() {
  const { lang } = useStore() as { lang: Lang };
  const t = T[lang] ?? T.en;
  const isRtl = lang === 'he';

  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = getConsent();
    setConsent(c);
    if (!c) {
      setShowBanner(true);
    } else {
      setAnalytics(c.analytics);
      setMarketing(c.marketing);
    }

    const handleOpen = () => openModal();
    window.addEventListener('open-cookie-settings', handleOpen);
    return () => window.removeEventListener('open-cookie-settings', handleOpen);
  }, []);

  function handleAcceptAll() {
    const c = acceptAll();
    setConsent(c);
    setShowBanner(false);
    setShowModal(false);
  }

  function handleRejectAll() {
    const c = rejectAll();
    setConsent(c);
    setShowBanner(false);
    setShowModal(false);
  }

  function handleSavePreferences() {
    const c = saveConsent(analytics, marketing);
    setConsent(c);
    setShowBanner(false);
    setShowModal(false);
  }

  function openModal() {
    if (consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
    setShowBanner(false);
    setShowModal(true);
  }

  if (!showBanner && !showModal) return null;

  return (
    <>
      {/* Banner */}
      {showBanner && (
        <div
          role="dialog"
          aria-label={t.modalTitle}
          dir={isRtl ? 'rtl' : 'ltr'}
          style={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            right: 20,
            zIndex: 9000,
            maxWidth: 580,
            marginInline: 'auto',
            background: SM.surface,
            borderRadius: 14,
            border: `1px solid ${SM.border}`,
            boxShadow: '0 14px 28px -14px rgba(99,91,255,.2)',
            padding: '18px 20px',
          }}
        >
          <p style={{ fontSize: 13, color: SM.textDim, margin: '0 0 14px', lineHeight: 1.6 }}>
            {t.bannerText}{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener"
              style={{ color: SM.primary, fontWeight: 600, textDecoration: 'none' }}
            >
              {t.privacyLink}
            </a>
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={handleAcceptAll}
              style={{
                background: SM.grad,
                color: '#fff',
                border: 'none',
                borderRadius: 9,
                padding: '9px 18px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(99,91,255,.35)',
              }}
            >
              {t.acceptAll}
            </button>
            <button
              onClick={handleRejectAll}
              style={{
                background: 'transparent',
                color: SM.textDim,
                border: `1px solid ${SM.border}`,
                borderRadius: 9,
                padding: '9px 18px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.rejectAll}
            </button>
            <button
              onClick={openModal}
              style={{
                background: SM.primarySoft,
                color: SM.primaryInk,
                border: 'none',
                borderRadius: 9,
                padding: '9px 18px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.manage}
            </button>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {showModal && (
        <>
          <div
            onClick={() => {
              setShowModal(false);
              if (!consent) setShowBanner(true);
            }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,48,.45)', zIndex: 9001 }}
          />
          <div
            role="dialog"
            aria-modal
            aria-label={t.modalTitle}
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              zIndex: 9002,
              width: '90%',
              maxWidth: 480,
              maxHeight: '85vh',
              overflow: 'auto',
              background: SM.surface,
              borderRadius: 16,
              border: `1px solid ${SM.border}`,
              boxShadow: '0 24px 48px -12px rgba(99,91,255,.25)',
              padding: '24px 24px 20px',
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: SM.text, marginBottom: 6 }}>
                {t.modalTitle}
              </div>
              <div style={{ fontSize: 12, color: SM.textDim, lineHeight: 1.55 }}>{t.modalSub}</div>
            </div>

            <CategoryRow
              title={t.essential}
              desc={t.essentialDesc}
              checked
              disabled
              alwaysOnLabel={t.alwaysOn}
            />
            <CategoryRow
              title={t.analytics}
              desc={t.analyticsDesc}
              checked={analytics}
              onChange={setAnalytics}
            />
            <CategoryRow
              title={t.marketing}
              desc={t.marketingDesc}
              checked={marketing}
              onChange={setMarketing}
            />

            <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={handleSavePreferences}
                style={{
                  background: SM.grad,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 9,
                  padding: '10px 22px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(99,91,255,.35)',
                }}
              >
                {t.save}
              </button>
              <button
                onClick={handleAcceptAll}
                style={{
                  background: SM.primarySoft,
                  color: SM.primaryInk,
                  border: 'none',
                  borderRadius: 9,
                  padding: '10px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t.acceptAll}
              </button>
              <button
                onClick={handleRejectAll}
                style={{
                  background: 'transparent',
                  color: SM.textDim,
                  border: `1px solid ${SM.border}`,
                  borderRadius: 9,
                  padding: '10px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t.rejectAll}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
